import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { SERVICE_AREAS } from '../config/areas';
import { SEO_PAGES } from '../config/seoPages';
import { useTheme } from '../contexts/ThemeContext';
import './IDXTownPage.css';

const AreaPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const area = SERVICE_AREAS.find(a => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => { document.body.style.overflow = ''; document.body.style.position = ''; };
  }, [isDarkMode, slug]);

  useEffect(() => {
    if (!area) return;
    setLoading(true);
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?zip=${area.zip}&state=${SITE_CONFIG.STATE}&firstPhoto=true&hasMedia=true&listingDateSort=newold`, {});
        if (response.ok) {
          const data = await response.json();
          setProperties(data.properties || []);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [area]);

  if (!area) {
    return (
      <div className="idxtown-page">
        <div className="idxtown-container">
          <button className="idxtown-home-btn" onClick={() => navigate('/')}>← Home</button>
          <h1>Area not found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="idxtown-page">
      <div className="idxtown-container">
        <button className="idxtown-home-btn" onClick={() => navigate('/')}>← Home</button>

        <header className="idxtown-header">
          <span className="idxtown-eyebrow">{area.name}, Arizona</span>
          <h1>{area.name} Homes for Sale: {area.tagline}</h1>
        </header>

        <section className="idxtown-vibe">
          <h2>Why {area.name}?</h2>
          <p>{area.description}</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Living in {area.name}</h2>
          <div className="amenities-list">
            {area.highlights.map((h, i) => (
              <div className="amenity" key={i}>
                <h3>{h.title}</h3>
                <p>{h.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="market-snapshot">
          <h2>{area.name} Real Estate Market Snapshot</h2>
          <p>The {area.name} market offers strong opportunities for buyers and sellers. Here's a current overview:</p>
          <table className="market-stats">
            <thead>
              <tr><th>Market Statistic</th><th>Value</th></tr>
            </thead>
            <tbody>
              <tr><td>Median Home Price</td><td>{area.marketStats.medianPrice}</td></tr>
              <tr><td>Average Days on Market</td><td>{area.marketStats.daysOnMarket}</td></tr>
              <tr><td>Popular Home Types</td><td>{area.marketStats.popularTypes}</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Current {area.name} Properties for Sale</h2>
          {loading ? (
            <p>Loading properties...</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 12).map((property) => (
                <div
                  key={property.id}
                  className="property-card"
                  onClick={() => window.location.href = `/listing/${property.listing_key}`}
                  style={{
                    backgroundImage: property.first_photo_url ? `url(${property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.list_price?.toLocaleString() || property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                </div>
              ))}
            </div>
          ) : (
            <p>No properties currently listed. Contact {SEO_CONFIG.AGENT_NAME} for off-market opportunities.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Explore {area.name}?</h2>
          <p>Contact {SEO_CONFIG.AGENT_NAME}, your trusted Arizona real estate expert, to schedule a tour of available {area.name} properties.</p>
          <div className="cta-buttons">
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=${area.name} Home Inquiry`} className="cta-button primary">
              Email {SEO_CONFIG.AGENT_NAME.split(' ')[0]}
            </a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`} className="cta-button secondary">
              Call {SITE_CONFIG.AGENT.PHONE}
            </a>
          </div>
        </section>

        {area.slug === 'maricopa' && (
          <section className="cta-section" style={{paddingTop: 0}}>
            <h2>Research Maricopa HOA Communities</h2>
            <p>Compare management contacts, historical fee estimates, and buyer due-diligence questions for Maricopa neighborhoods.</p>
            <Link to="/maricopa-hoas" className="cta-button primary">Open the HOA directory</Link>
          </section>
        )}

        <section style={{marginTop: '40px', paddingTop: '30px', borderTop: '1px solid rgba(190,146,100,0.3)'}}>
          <h3 style={{color: '#d29732', marginBottom: '15px'}}>Maricopa Real Estate Guides</h3>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px 16px'}}>
            {SEO_PAGES.map(p => (
              <Link key={p.slug} to={`/guide/${p.slug}`} style={{color: '#ccc', textDecoration: 'underline', fontSize: '13px', lineHeight: '1.8'}}>
                {p.title.split('|')[0].trim()}
              </Link>
            ))}
          </div>
        </section>
      </div>
      <div style={{background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
        <img src={SITE_CONFIG.MLS.LOGO_URL} alt={SITE_CONFIG.MLS.SHORT_NAME} style={{height: '20px', objectFit: 'contain'}} />
        <span style={{fontSize: '10px', color: '#ccc', flex: 1}}>{SITE_CONFIG.MLS.DISCLAIMER} | Agent License: {SITE_CONFIG.AGENT.LICENSE}</span>
      </div>
    </div>
  );
};

export default AreaPage;
